import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { api } from '@/lib/api/client'

/**
 * LMS sync — Graviton-tier tenants read loans and dues from the lender's LMS
 * instead of Excel uploads. `mode` is the tenant's `lms.mode` setting;
 * UNCONFIGURED means it is unset and nothing syncs.
 */
export interface LmsSyncRun {
  runId: string
  trigger: 'NIGHTLY' | 'MANUAL'
  startedAt: string
  finishedAt: string | null
  refreshed: number
  closed: number
  failed: number
  lastError: string | null
}

export interface LmsStatus {
  mode: 'MOCK' | 'LIVE' | 'UNCONFIGURED'
  lastRun: LmsSyncRun | null
}

/** `refreshed: false` means the stored figures stand; `reason` says why. */
export interface CaseLmsRefresh {
  refreshed: boolean
  reason: null | 'not_configured' | 'recent' | 'lms_unavailable'
  caseStatus: string
  asOfAt: string | null
}

/**
 * One loan as the mock LMS holds it. Amounts are BigDecimal on the API and
 * arrive as JSON numbers — callers coerce with Number().
 */
export interface MockLmsLoan {
  asOfDate: string
  asOfAt: string
  dpd: number
  bucket: string
  outstandingPrincipal: number | string
  overduePrincipal: number | string
  overdueInterest: number | string
  lppCharges: number | string
  bounceCharges: number | string
  lastPaymentDate: string | null
  lastPaymentAmount: number | string | null
}

const STATUS_KEY = ['lms', 'status']

export function useLmsStatus(enabled = true) {
  return useQuery({
    queryKey: STATUS_KEY,
    enabled,
    queryFn: async () => (await api.get<LmsStatus>('/v1/lms/status')).data,
  })
}

/** Re-reads every open case from the LMS. Figures change everywhere, so invalidate broadly. */
export function useLmsRefreshAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post<LmsSyncRun>('/v1/lms/refresh')).data,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['freshness'] })
      qc.invalidateQueries({ queryKey: STATUS_KEY })
    },
  })
}

/** Refreshes one case from the LMS. Variables carry what the caller needs to read the outcome. */
export function useCaseLmsRefresh() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ caseId }: { caseId: string; wasOpen: boolean }) =>
      (await api.post<CaseLmsRefresh>(`/v1/cases/${caseId}/lms-refresh`)).data,
    onSuccess: (res, { caseId }) => {
      if (!res.refreshed) return
      qc.invalidateQueries({ queryKey: ['cases', caseId] })
      qc.invalidateQueries({ queryKey: ['freshness'] })
    },
  })
}

export function useMockLmsLoan() {
  return useMutation({
    mutationFn: async (loanNo: string) =>
      (await api.get<MockLmsLoan>(`/v1/lms/mock/loans/${encodeURIComponent(loanNo)}`)).data,
  })
}

/** Simulates the customer paying at the lender's branch. No amount = everything due. */
export function useMockLenderPayment() {
  return useMutation({
    mutationFn: async ({ loanNo, amount }: { loanNo: string; amount?: number | undefined }) =>
      (await api.post<MockLmsLoan>(
        `/v1/lms/mock/loans/${encodeURIComponent(loanNo)}/lender-payment`,
        amount == null ? {} : { amount },
      )).data,
  })
}

/** The API's error text (ResponseStatusException → ProblemDetail `detail`), e.g. "not_in_lms: …". */
export function lmsErrorDetail(err: unknown): string {
  if (!isAxiosError(err)) return ''
  const data = err.response?.data as { detail?: string; message?: string } | undefined
  return data?.detail ?? data?.message ?? ''
}
